import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import { FP, ConfirmarDialog } from "components/financepro";

/**
 * Confirmación de cancelación de préstamo, con la pregunta por el motor.
 *
 * Cancelar siempre registra el incauto del préstamo; lo que hay que decidir
 * es dónde queda el chasis. Antes no se preguntaba y el motor se quedaba en
 * estado "vendido" para siempre: no aparecía en el selector de cobro de
 * inicial y solo se recuperaba con el reingreso manual de Registro de chasis.
 *
 * La respuesta viaja al backend como `devolverinventario` ("S"/"N") y es él
 * quien coloca el estado del producto. En préstamos personales (sin motor)
 * pase `tieneChasis={false}`: no hay nada que preguntar.
 *
 *   <CancelarPrestamoDialog abierto={open} noprest={11810} cliente="Juan Pérez"
 *     chasis={sel.producto_chasis} onCerrar={...}
 *     onConfirmar={(devolver) => cancelar(devolver)} />
 */

const OPCIONES = [
  {
    valor: "S",
    titulo: "Sí, devolverlo al inventario",
    detalle: "Queda disponible para cobrarle inicial y volver a facturarlo.",
  },
  {
    valor: "N",
    titulo: "No, dejarlo incautado",
    detalle: "El incauto queda abierto y se libera desde Incautos/cargos vigentes.",
  },
];

export default function CancelarPrestamoDialog({
  abierto,
  noprest,
  cliente,
  chasis,
  tieneChasis = true,
  // Contexto extra de la pantalla que lo abre (balance pendiente, permisos…),
  // pintado debajo del encabezado. No repita aquí el número ni el cliente.
  detalle = null,
  cargando = false,
  onCerrar,
  onConfirmar,
}) {
  const [devolver, setDevolver] = useState("");

  // Sin preselección: la decisión es del usuario, no un default que se cuela
  // por pulsar Enter. En préstamos personales no hay pregunta que responder.
  useEffect(() => {
    if (abierto) setDevolver("");
  }, [abierto]);

  return (
    <ConfirmarDialog
      abierto={abierto}
      titulo="Cancelar préstamo"
      destructivo
      textoConfirmar="Cancelar préstamo"
      textoCerrar="Volver"
      cargando={cargando}
      confirmarDeshabilitado={tieneChasis && !devolver}
      onCerrar={onCerrar}
      onConfirmar={() => onConfirmar(devolver === "S")}
    >
      <Typography sx={{ fontSize: "0.92rem", color: FP.color.textPrimary }}>
        Se cancelará el préstamo <b>No. {noprest}</b>
        {cliente ? (
          <>
            {" de "}
            <b>{cliente}</b>
          </>
        ) : null}
        .
      </Typography>
      {detalle ? (
        // Typography y no Box: la tipografía se declara aquí y no se hereda
        // de lo que haya montado la pantalla que abre el diálogo.
        <Typography
          component="div"
          sx={{ mt: 0.75, fontSize: "0.82rem", color: FP.color.textSecondary }}
        >
          {detalle}
        </Typography>
      ) : null}

      {tieneChasis ? (
        <FormControl fullWidth sx={{ mt: 2.5 }}>
          <FormLabel
            sx={{
              fontSize: "0.9rem",
              fontWeight: 700,
              color: FP.color.textPrimary,
              "&.Mui-focused": { color: FP.color.textPrimary },
            }}
          >
            ¿Devolver el chasis al inventario?
          </FormLabel>
          {chasis ? (
            <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary, mt: 0.25 }}>
              Chasis {chasis}
            </Typography>
          ) : null}
          <RadioGroup
            value={devolver}
            onChange={(e) => setDevolver(e.target.value)}
            sx={{ mt: 1.25, gap: 1 }}
          >
            {OPCIONES.map((o) => {
              const activa = devolver === o.valor;
              return (
                <FormControlLabel
                  key={o.valor}
                  value={o.valor}
                  disableTypography
                  control={<Radio size="small" sx={{ py: 0, mr: 0.75 }} />}
                  label={
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: "0.85rem", fontWeight: 700, color: FP.color.textPrimary }}
                      >
                        {o.titulo}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.76rem", color: FP.color.textSecondary, mt: 0.15 }}
                      >
                        {o.detalle}
                      </Typography>
                    </Box>
                  }
                  // m:0 anula el margen negativo que MUI le pone al control y
                  // que descuadraba la fila; alignItems arriba para que el
                  // radio quede a la altura del título, no del centro del texto.
                  sx={{
                    m: 0,
                    alignItems: "flex-start",
                    p: 1.25,
                    borderRadius: 1.4,
                    border: `1px solid ${activa ? FP.color.accentBlue : FP.color.border}`,
                    bgcolor: activa ? FP.color.accentBlueSoft : "transparent",
                    transition: "border-color .15s, background-color .15s",
                    "&:hover": { borderColor: activa ? FP.color.accentBlue : FP.color.borderStrong },
                  }}
                />
              );
            })}
          </RadioGroup>
        </FormControl>
      ) : null}
    </ConfirmarDialog>
  );
}
